import { pxToRem } from '@creditornot/cb-utils';

import { typographyCss as coreTypographyCss } from './typographyCss';

const baseHeadingStyle = coreTypographyCss.baseHeadingStyle();
const baseBodyStyle = coreTypographyCss.baseBodyStyle();

type FontWeight = 'normal' | 'bold' | 'semiBold';

const fontWeights: Record<FontWeight, number> = {
  normal: 400,
  semiBold: 500,
  bold: 700,
};

interface DecoratedFontStyleOptions {
  /** Do not add rules for nested `b` and `strong` elements. Use for example when applying font styles to pseudo-selectors.  */
  disableNestedWeightTags?: boolean;
  weight?: FontWeight;
}

function decoratedFontStyle(cssStyle: string) {
  return ({ disableNestedWeightTags, weight = 'normal' }: DecoratedFontStyleOptions = {}) => {
    // prettier-ignore
    return `
      ${cssStyle}
      font-weight: ${fontWeights[weight]};

      ${disableNestedWeightTags ? '' : `
        b, strong {
          font-weight: ${weight === 'semiBold' || weight === 'bold' ? '700' : '600'};
        }`
      }
    `;
  };
}

/**
 * @deprecated Use `typographyCss` or `typographyClassName` from `@creditornot/cb-ingredients` instead.
 *
 * Legacy text styles for consumer projects.
 */
export const legacyConsumerTypographyCss = {
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements: `typographyCss.display` or `typographyClassName.display`.
   */
  HeadingExtraLarge: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(60)};
    line-height: ${pxToRem(69)};
    font-weight: 700;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements: `typographyCss.largeTitle` or `typographyClassName.largeTitle`.
   */
  HeadingLarge: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(46)};
    line-height: ${pxToRem(56)};
    font-weight: 700;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements: `typographyCss.title` or `typographyClassName.title`.
   */
  HeadingMedium: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(32)};
    line-height: ${pxToRem(40)};
    font-weight: 700;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements: `typographyCss.title` or `typographyClassName.title`.
   */
  HeadingSmall: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(28)};
    line-height: ${pxToRem(36)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements: `typographyCss.headline` or `typographyClassName.headline`.
   */
  HeadingTiny: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(20)};
    line-height: ${pxToRem(24)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements:
   * * `typographyCss.body`
   * * `typographyCss.bodyLabel`
   * * `typographyClassName.body`
   * * `typographyClassName.bodyLabel`
   */
  Body1: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(18)};
    line-height: ${pxToRem(24)};
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `),
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements:
   * * `typographyCss.body`
   * * `typographyCss.bodyLabel`
   * * `typographyClassName.body`
   * * `typographyClassName.bodyLabel`
   */
  Body2: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(24)};
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `),
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements:
   * * `typographyCss.caption`
   * * `typographyCss.captionLabel`
   * * `typographyClassName.caption`
   * * `typographyClassName.captionLabel`
   */
  Body3: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `),
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements:
   * * `typographyCss.footnote`
   * * `typographyCss.footnoteLabel`
   * * `typographyClassName.footnote`
   * * `typographyClassName.footnoteLabel`
   */
  Small: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `),
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * Possible replacements:
   * * `typographyCss.footnote`
   * * `typographyCss.footnoteLabel`
   * * `typographyClassName.footnote`
   * * `typographyClassName.footnoteLabel`
   */
  Tiny: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(10)};
    line-height: ${pxToRem(12)};
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `),
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * There are no suggested replacements in the new styles.
   */
  Overline1: () => `
    ${baseBodyStyle}
    font-weight: 500;
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    letter-spacing: ${pxToRem(0.5)};
    text-transform: uppercase;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * There are no suggested replacements in the new styles.
   */
  Overline2: () => `
    ${baseBodyStyle}
    font-weight: 500;
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    letter-spacing: ${pxToRem(0.5)};
    font-style: normal;
    font-stretch: normal;
    text-transform: uppercase;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * There are no suggested replacements in the new styles.
   */
  Overline3: () => `
    ${baseBodyStyle}
    font-weight: 500;
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    letter-spacing: ${pxToRem(0.5)};
    font-style: normal;
    font-stretch: normal;
    text-transform: uppercase;
  `,
  /**
   * @deprecated This style is deprecated. Please do not use for new features.
   *
   * There are no suggested replacements in the new styles.
   */
  Overline4: () => `
    ${baseBodyStyle}
    font-weight: 500;
    font-size: ${pxToRem(10)};
    line-height: ${pxToRem(12)};
    letter-spacing: ${pxToRem(0.5)};
    font-style: normal;
    font-stretch: normal;
    text-transform: uppercase;
  `,
} satisfies Record<string, () => string>;
